import { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../context/SocketContext';
import { useNavigate } from 'react-router-dom';

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'A l\'instant';
  if (diff < 3600) return Math.floor(diff/60) + ' min';
  if (diff < 86400) return Math.floor(diff/3600) + 'h';
  if (diff < 604800) return Math.floor(diff/86400) + 'j';
  return new Date(date).toLocaleDateString('fr-FR');
}

const TYPE_ICON = { friend_request: '👤', friend_accepted: '👤', mention: '@', message: '✉', comment: '💬' };
const TYPE_COLOR = { friend_request: '#8b1a2a', friend_accepted: '#1a6a30', mention: '#1a4a5a', message: '#1a6a30', comment: '#8b1a2a' };

export default function NotificationBell({ token }) {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [extraUnread, setExtraUnread] = useState(0);
  const ref = useRef();
  const socketRef = useRef(null);
  const navigate = useNavigate();

  const unread = notifs.filter(n => !n.read).length + extraUnread;

  const fetchNotifs = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/notifications', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (Array.isArray(data)) { setNotifs(data); setExtraUnread(0); }
    } catch {}
  };

  // Polling 30s
  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const globalSocketRef = useSocket();

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      const socket = globalSocketRef?.current;
      if (!socket?.connected) return;
      socket.on('new-notification', () => { setExtraUnread(prev => prev + 1); fetchNotifs(); });
      socket.on('new-message', () => { setExtraUnread(prev => prev + 1); });
      clearInterval(interval);
    }, 300);
    return () => {
      clearInterval(interval);
      const socket = globalSocketRef?.current;
      if (socket) { socket.off('new-notification'); socket.off('new-message'); }
    };
  }, [token, globalSocketRef]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleReadAll = async () => {
    await fetch('/api/notifications/read-all', { method: 'PATCH', headers: { Authorization: 'Bearer ' + token } });
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setExtraUnread(0);
  };

  const handleClick = async (notif) => {
    await fetch('/api/notifications/' + notif._id + '/read', { method: 'PATCH', headers: { Authorization: 'Bearer ' + token } });
    setNotifs(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
    setOpen(false);
    if (notif.type === 'message') navigate('/profil?tab=messages');
    else if (notif.link) navigate(notif.link);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => { setOpen(!open); if (!open) { setExtraUnread(0); fetchNotifs(); } }}
        style={{ position: 'relative', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, width: 36, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16 }}>
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, background: '#c03030', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, border: '2px solid #14080c' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 42, right: 0, width: 360, background: '#fff', border: '1px solid #e8d0d0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 500, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e8d0d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, color: '#1a1010' }}>Notifications</div>
            {unread > 0 && <div onClick={handleReadAll} style={{ fontSize: 12, color: '#8b1a2a', cursor: 'pointer' }}>Tout marquer comme lu</div>}
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9a8080', fontSize: 13, fontStyle: 'italic' }}>Aucune notification</div>
            ) : (
              notifs.map(n => {
                const initiales = (n.sender?.username || 'U').slice(0,2).toUpperCase();
                return (
                  <div key={n._id} onClick={() => handleClick(n)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderBottom: '1px solid #f5eeeb', cursor: 'pointer', background: n.read ? '#fff' : '#fff8f5' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e8c0c8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#8b1a2a', overflow: 'hidden' }}>
                        {n.sender?.photo ? <img src={n.sender.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initiales}
                      </div>
                      <div style={{ position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: TYPE_COLOR[n.type], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff', border: '2px solid #fff' }}>
                        {TYPE_ICON[n.type]}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#1a1010', lineHeight: 1.5, marginBottom: 2 }}>
                        <strong>{n.sender?.username}</strong> {n.text}
                      </div>
                      <div style={{ fontSize: 12, color: '#9a8080' }}>{timeAgo(n.createdAt)}</div>
                    </div>
                    {!n.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#8b1a2a', flexShrink: 0, marginTop: 4 }}></div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
